import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getFailedEffects, retryFailedEffectsByCategory } from '@/lib/syncQueue';
import { track } from '@/lib/telemetry';
import { toast } from '@/lib/toast';

/**
 * Listens for the browser `online` event and auto-retries dead-lettered
 * effects whose failure category was `network`. Pairs with the manual
 * Retry/Discard surface in `FailedSyncRecovery`: if the *only* reason a
 * mutation parked was that the network was down, the user shouldn't have to
 * go look for it once they're back online.
 *
 * Defensive choices:
 *  - **Cooldown of 30s between auto-retries.** A flapping connection (Wi-Fi
 *    handoff, transit) fires `online` repeatedly; we don't want to chew
 *    through every retry budget the moment connectivity twitches.
 *  - **Only retries `network`-category entries.** Other categories (auth,
 *    permission, validation, server) need root-cause action — re-queueing
 *    them on a connectivity change just sends them straight back to the
 *    dead-letter queue.
 *  - **Toast is informational, not an error.** The user didn't ask for this
 *    retry; we're telling them we did it on their behalf.
 *  - **No-op when nothing matches.** Boot of the app on an online browser
 *    won't fire `online`, but if a stray fire happens with an empty queue,
 *    we silently bail — no spurious toasts.
 */

const COOLDOWN_MS = 30_000;

export function FailedSyncAutoRetry(): null {
  const { t } = useTranslation();
  const lastRunRef = useRef<number>(0);

  useEffect(() => {
    const handler = () => {
      const now = Date.now();
      if (now - lastRunRef.current < COOLDOWN_MS) return;

      const networkParked = getFailedEffects().filter(
        (e) => /failed to fetch|network|offline|timeout|timed out|econnrefused|enotfound|socket hang up/i.test(e.lastError || '')
      );
      if (networkParked.length === 0) return;

      const retried = retryFailedEffectsByCategory('network');
      if (retried === 0) return;

      lastRunRef.current = now;
      // Telemetry: cross-reference with subsequent `offline_sync_parked` events
      // to measure how many of these retried effects actually drained vs. re-parked.
      track('offline_sync_auto_retried', { count: retried, category: 'network' });
      toast.success(t('header.failedSyncAutoRetry.title'), {
        description: t('header.failedSyncAutoRetry.description', { count: retried }),
        id: 'failed-sync-auto-retry',
      });
    };

    window.addEventListener('online', handler);
    return () => window.removeEventListener('online', handler);
  }, [t]);

  return null;
}
